import React, { ChangeEvent, useEffect, useState } from "react";
import AppBar from "../components/AppBar";
import CustomInput from "../components/CustomInput";
import FormSection, { FormRow } from "../components/form-section";
import CustomRadioButton from "../components/CustomRadioButton";
import CustomSelect from "../components/CustomSelect";
import Modals from "../components/Modal";
import CustomButton from "../components/CustomButton";
import { ParseErrors } from "../utils/ParseErrors";
import axiosInstance from "../utils/axiosInstance";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Loader from "../components/Loader";
import { useDebounceCallback } from "usehooks-ts";
import {
  brandCodes,
  branddata,
  loanStatusData,
} from "../components/chaseListComponents/chaseListData";
import GetCancellationReason, {
  cancellationReasons,
} from "../utils/get-cancellation-reason";
import CanculateChaseOutstandingBalance from "../utils/canculate-chase-outstanding-balance";

const CreateChase: React.FC = () => {
  const [values, setValues] = useState<Record<string, any>>({
    bussinessType: "Sales",
    policyType: "Standard",
    paymentType: "Cash",
    status: "Awaiting-Customer",
    brand: "VLD",
    cancelFee: 45.0,
    age: "new",
  });
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [outStandingAmount, setOutstandingAmount] = useState(0);

  // --------------------------------- [ HANDLE SUBMIT ]-------------------------------------

  const createChase = async () => {
    if (validateChaseForm()) {
      setLoading(true);
      try {
        await axiosInstance.post("/loan", {
          ...values,
          outstandingBalance: outStandingAmount,
        });
        toast.success("Loan created successfully");
        navigate("/chaselist");
        setLoading(false);
      } catch (error) {
        toast.error(ParseErrors(error));
        setLoading(false);
        console.log(error);
      }
    }
  };

  // ---------------------------------- [ UPDATE INPUT VALUES ] --------------------------------

  const HandleChangeValues = useDebounceCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setValues((prev) => ({
        ...prev,
        [e.target.name]:
          e.target.type === "number" ? e.target.valueAsNumber : e.target.value,
      }));
    },
    200
  );

  const validateDate = (date: string) => {
    return date?.split("-")[0]?.startsWith("0") ? false : true;
  };

  // ----------------------------------- [ VALIDATE FUNCTIONS ] --------------------------------

  const validateChaseForm = () => {
    if (!values?.firstName) {
      toast.error("Please enter First Name");
      return false;
    } else if (!values?.surname) {
      toast.error("Please enter Surname");
      return false;
    } else if (!values?.email) {
      toast.error("Please enter Email");
      return false;
    } else if (!values?.phone) {
      toast.error("Please enter Phone");
      return false;
    } else if (!values?.policyReferenceNumber) {
      toast.error("Please enter Policy Reference Number");
      return false;
    } else if (!values?.policyType) {
      toast.error("Please Select Policy Type");
      return false;
    } else if (!values?.bussinessType) {
      toast.error("Please enter Bussiness Type");
      return false;
    } else if (!values?.brandCode) {
      toast.error("Please enter Affinity");
      return false;
    } else if (
      !validateDate(values?.cancellationDate) ||
      !validateDate(values?.policyStartDate)
    ) {
      toast.error("Please verify all dates are in correct format");
      return false;
    } else {
      return true;
    }
  };

  useEffect(() => {
    setOutstandingAmount(
      CanculateChaseOutstandingBalance({
        insurerRefund: values?.insurerRefund || 0,
        reclaimedInceptionDiscount: values?.reclaimedInceptionDiscount || 0,
        cancelFee: values?.cancelFee || 0,
        unusedAddOnsRefund: values?.unusedAddOnsRefund || 0,
        outstandingBalanceofFinanceAgreement:
          values?.outstandingBalanceofFinanceAgreement || 0,
        partialPaymentAmount: values?.partialPaymentAmount || 0,
      })
    );
  }, [values]);

  return (
    <>
      <div className="sticky top-0 z-20 w-full border-b border-edge">
        <AppBar title="DocuSure" profileInfo />
      </div>

      <Modals
        showDialog={true}
        onCloseDialog={() => console.log("e")}
        title="Create chase"
        subtitle="Enter the customer, policy and amount details below."
        width="880"
        centerTitle
        backButton
      >
        <div className="flex flex-col gap-4 px-5 py-4">
          {/* ---------------------------------------------- CUSTOMER ---------------------------------------------- */}
          <FormSection title="Customer" hint="Who the chase is for and how to reach them.">
            <CustomInput
              autoComplete="none"
              label="First Name"
              defaultValue={values?.firstName}
              name="firstName"
              onChange={HandleChangeValues}
            />
            <CustomInput
              autoComplete="none"
              label="Surname"
              defaultValue={values?.surname}
              name="surname"
              onChange={HandleChangeValues}
            />
            <CustomInput
              label="Email Address"
              autoComplete="none"
              defaultValue={values?.email}
              name="email"
              onChange={HandleChangeValues}
            />
            <CustomInput
              label="Phone Number"
              autoComplete="none"
              defaultValue={values?.phone}
              name="phone"
              onChange={HandleChangeValues}
            />
            <CustomInput
              label="Date of Birth"
              autoComplete="none"
              defaultValue={values?.dob}
              name="dob"
              type="date"
              onChange={HandleChangeValues}
            />
            <CustomInput
              label="Post Code"
              autoComplete="none"
              defaultValue={values?.postCode}
              name="postCode"
              className="uppercase"
              onChange={HandleChangeValues}
            />
          </FormSection>

          {/* ---------------------------------------------- ADDRESS ---------------------------------------------- */}
          <FormSection title="Address">
            <CustomInput
              label="Address 1"
              autoComplete="none"
              defaultValue={values?.address1}
              name="address1"
              onChange={HandleChangeValues}
            />
            <CustomInput
              label="Address 2"
              autoComplete="none"
              defaultValue={values?.address2}
              name="address2"
              onChange={HandleChangeValues}
            />
            <CustomInput
              label="Address 3"
              autoComplete="none"
              defaultValue={values?.address3}
              name="address3"
              onChange={HandleChangeValues}
            />
            <CustomInput
              label="Address 4"
              autoComplete="none"
              defaultValue={values?.address4}
              name="address4"
              onChange={HandleChangeValues}
            />
            <CustomInput
              label="Address 5"
              autoComplete="none"
              defaultValue={values?.address5}
              name="address5"
              onChange={HandleChangeValues}
            />
          </FormSection>

          {/* ---------------------------------------------- POLICY ---------------------------------------------- */}
          <FormSection title="Policy" hint="Where the chase came from and what it relates to.">
            <CustomSelect
              comboboxType="select"
              label="Chase Status"
              labelProperty="name"
              showTop
              valueProperty="name"
              options={loanStatusData}
              onSingleSelectChange={(option) =>
                setValues((prev) => ({ ...prev, status: option?.name }))
              }
              value={loanStatusData.find((type) => type.name === values?.status)}
            />
            <CustomSelect
              comboboxType="select"
              label="Brand"
              labelProperty="label"
              showTop
              valueProperty="name"
              options={branddata}
              onSingleSelectChange={(option) =>
                setValues((prev) => ({ ...prev, brand: option?.name }))
              }
              value={branddata.find((type) => type.name === values?.brand)}
            />
            <CustomSelect
              comboboxType="select"
              label="Affinity"
              labelProperty="value"
              showTop
              valueProperty="value"
              options={brandCodes}
              onSingleSelectChange={(option) =>
                setValues((prev) => ({ ...prev, brandCode: option?.value }))
              }
              value={brandCodes.find((type) => type.value === values?.brandCode)}
            />
            <CustomInput
              label="Agent Code"
              autoComplete="none"
              defaultValue={values?.schemeCode}
              name="schemeCode"
              onChange={HandleChangeValues}
            />
            <CustomInput
              label="Policy Reference Number"
              autoComplete="none"
              defaultValue={values?.policyReferenceNumber}
              name="policyReferenceNumber"
              onChange={HandleChangeValues}
            />
            <CustomInput
              label="Insurer Name"
              autoComplete="none"
              defaultValue={values?.insurerName}
              name="insurerName"
              onChange={HandleChangeValues}
            />
            <CustomInput
              label="Policy Start Date"
              autoComplete="none"
              defaultValue={values?.policyStartDate}
              name="policyStartDate"
              error={!validateDate(values?.policyStartDate) && "Invalid Date"}
              type="date"
              onChange={HandleChangeValues}
            />
            <CustomInput
              label="Cancellation Date"
              autoComplete="none"
              defaultValue={values?.cancellationDate}
              name="cancellationDate"
              type="date"
              error={!validateDate(values?.cancellationDate) && "Invalid Date"}
              onChange={HandleChangeValues}
            />
            <CustomInput
              label="Vehicle Reg No"
              autoComplete="none"
              defaultValue={values?.vehicleReg}
              name="vehicleReg"
              className="uppercase font-heading-font"
              onChange={HandleChangeValues}
            />
            <CustomSelect
              comboboxType="select"
              label="Reason For Cancellation"
              labelProperty="label"
              showTop
              valueProperty="code"
              options={cancellationReasons}
              onSingleSelectChange={(option) =>
                setValues((prev) => ({
                  ...prev,
                  reasonForCancellation: option?.code,
                }))
              }
              value={GetCancellationReason(values.reasonForCancellation)}
            />
          </FormSection>

          {/* ---------------------------------------------- AMOUNTS ---------------------------------------------- */}
          <FormSection
            title="Amounts"
            hint="Outstanding balance is calculated from the figures above."
          >
            <CustomInput
              label="Insurer Refund"
              autoComplete="none"
              defaultValue={values?.insurerRefund}
              name="insurerRefund"
              type="number"
              onChange={HandleChangeValues}
              forceDecimal
              startIcon="£"
            />
            <CustomInput
              label="Reclaimed Inception Discount"
              autoComplete="none"
              defaultValue={values?.reclaimedInceptionDiscount}
              name="reclaimedInceptionDiscount"
              type="number"
              onChange={HandleChangeValues}
              forceDecimal
              negativeLabel
              startIcon="£"
            />
            <CustomInput
              label="Outstanding Balance of Finance Agreement"
              autoComplete="none"
              defaultValue={values?.outstandingBalanceofFinanceAgreement}
              name="outstandingBalanceofFinanceAgreement"
              type="number"
              onChange={HandleChangeValues}
              forceDecimal
              negativeLabel
              startIcon="£"
            />
            <CustomInput
              label="Cancellation Fee"
              autoComplete="none"
              defaultValue={values?.cancelFee?.toFixed(2)}
              name="cancelFee"
              type="number"
              onChange={HandleChangeValues}
              forceDecimal
              negativeLabel
              startIcon="£"
            />
            <CustomInput
              label="Partial Payment Amount"
              autoComplete="none"
              defaultValue={values?.partialPaymentAmount}
              name="partialPaymentAmount"
              type="number"
              onChange={HandleChangeValues}
              forceDecimal
              startIcon="£"
            />
            <CustomInput
              label="Commissions/Addons Refund"
              autoComplete="none"
              defaultValue={values?.unusedAddOnsRefund}
              name="unusedAddOnsRefund"
              type="number"
              onChange={HandleChangeValues}
              forceDecimal
              startIcon="£"
            />
            <FormRow>
              <CustomInput
                label="Outstanding Balance"
                autoComplete="none"
                value={Math.abs(outStandingAmount)?.toFixed(2)}
                name="outstandingBalance"
                type="number"
                onChange={HandleChangeValues}
                forceDecimal
                negativeLabel={outStandingAmount < 0}
                startIcon="£"
                error={
                  Number(outStandingAmount.toFixed(2)) > 0
                    ? "Invalid Outstanding Balance"
                    : null
                }
              />
            </FormRow>
          </FormSection>

          {/* ---------------------------------------------- CLASSIFICATION ---------------------------------------------- */}
          <FormSection title="Classification">
            <div>
              <label className="text-[12px] font-medium text-light-gray block mb-1.5">
                Policy Type
              </label>
              <div className="flex items-center gap-5 h-10 max-sm:flex-wrap max-sm:h-auto max-sm:gap-3 max-sm:py-2">
                <CustomRadioButton
                  id="Nonstandard"
                  name="policyType"
                  label="Standard"
                  value="Standard"
                  checked={values?.policyType === "Standard"}
                  onChange={HandleChangeValues}
                />
                <CustomRadioButton
                  id="standard"
                  name="policyType"
                  label="Non-Standard"
                  checked={values?.policyType === "Non-Standard"}
                  onChange={HandleChangeValues}
                  value="Non-Standard"
                />
              </div>
            </div>

            <div>
              <label className="text-[12px] font-medium text-light-gray block mb-1.5">
                Department
              </label>
              <div className="flex items-center gap-5 h-10 max-sm:flex-wrap max-sm:h-auto max-sm:gap-3 max-sm:py-2">
                <CustomRadioButton
                  id="sales"
                  name="bussinessType"
                  label="Sales"
                  checked={values?.bussinessType === "Sales"}
                  onChange={HandleChangeValues}
                  value="Sales"
                />
                <CustomRadioButton
                  id="Renewals"
                  name="bussinessType"
                  label="Renewals"
                  value="Renewals"
                  checked={values?.bussinessType === "Renewals"}
                  onChange={HandleChangeValues}
                />
              </div>
            </div>

            <div>
              <label className="text-[12px] font-medium text-light-gray block mb-1.5">
                Chase Age
              </label>
              <div className="flex items-center gap-5 h-10 max-sm:flex-wrap max-sm:h-auto max-sm:gap-3 max-sm:py-2">
                <CustomRadioButton
                  id="new"
                  name="age"
                  label="New"
                  checked={values?.age === "new"}
                  onChange={HandleChangeValues}
                  value="new"
                />
                <CustomRadioButton
                  id="old"
                  name="age"
                  label="Old"
                  value="old"
                  checked={values?.age === "old"}
                  onChange={HandleChangeValues}
                />
              </div>
            </div>
          </FormSection>
        </div>

        {/* Sticky footer: the form is long enough that a button at the very bottom
            means scrolling back down to submit after fixing anything. */}
        <div className="sticky bottom-0 z-10 border-t border-edge bg-surface px-5 py-4">
          <CustomButton
            variant="primary"
            fullWidth
            onClick={createChase}
            disabled={loading || Number(outStandingAmount.toFixed(2)) > 0}
          >
            {loading ? <Loader /> : "Create Chase"}
          </CustomButton>
        </div>
      </Modals>
    </>
  );
};

export default CreateChase;
